import { useMemo } from 'react';
import { useSettings } from '@wu/widget-core';
import tinycolor from 'tinycolor2';

export enum ColorSchemeCSSVariable {
  StandBackground = '--standBackground',
  DiskBackground = '--diskBackground',
  CapBackground = '--capBackground',
  EvenSectorBackground = '--evenSectorBackground',
  OddSectorBackground = '--oddSectorBackground',
  SectorBackgroundGradientStart = '--sectorBackgroundGradientStart',
  SectorBackgroundGradientEnd = '--sectorBackgroundGradientEnd',
  PointerTopLayerBackground = '--pointerTopLayerBackground',
  PointerTopLayerBorder = '--pointerTopLayerBorder',
  PointerBottomLayerBackground = '--pointerBottomLayerBackground',
  Text = '--text'
}

enum Mode {
  Light,
  Dark,
  White
}

export function useColorScheme() {
  const { accentColor } = useSettings();
  return useMemo(() => {
    let mode: Mode;
    const isWhite = tinycolor(accentColor).toName() === 'white';
    if (isWhite) {
      mode = Mode.White;
    } else {
      mode = tinycolor(accentColor).isLight() ? Mode.Light : Mode.Dark;
    }

    const standBackground = tinycolor(accentColor).setAlpha(0.2).toString();

    const capBackground = tinycolor(accentColor).setAlpha(0.2).toString();

    const pointerTopLayerBackground = accentColor;
    const pointerTopLayerBorder = tinycolor(accentColor)
      .brighten(-20)
      .toString();
    const pointerBottomLayerBackground = tinycolor(accentColor)
      .setAlpha(0.2)
      .toString();

    let diskBackground: string;
    let evenSectorBackground: string;
    let oddSectorBackground: string;
    let sectorBackgroundGradientStart: string;
    let sectorBackgroundGradientEnd: string;
    let text: string;

    switch (mode) {
      case Mode.White:
        diskBackground = '#ccc';
        evenSectorBackground = '#fff';
        oddSectorBackground = '#f5f5f5';
        sectorBackgroundGradientStart = '#e5e5e5';
        sectorBackgroundGradientEnd = 'rgba(255, 255, 255, 0.4)';
        text = '#000';
        break;
      case Mode.Light:
        diskBackground = tinycolor(accentColor).brighten(-20).toString();
        evenSectorBackground = accentColor;
        oddSectorBackground = tinycolor.mix('#fff', accentColor, 60).toString();
        sectorBackgroundGradientStart = tinycolor(accentColor)
          .saturate(-95)
          .brighten(20)
          .setAlpha(0.4)
          .toString();
        sectorBackgroundGradientEnd = tinycolor(accentColor)
          .brighten(-15)
          .setAlpha(0.4)
          .toString();
        text = tinycolor(accentColor).brighten(-70).toString();
        break;
      case Mode.Dark:
        diskBackground = tinycolor(accentColor).brighten(-20).toString();
        evenSectorBackground = accentColor;
        oddSectorBackground = tinycolor.mix('#fff', accentColor, 70).toString();
        sectorBackgroundGradientStart = tinycolor(accentColor)
          .brighten(20)
          .setAlpha(0.5)
          .toString();
        sectorBackgroundGradientEnd = tinycolor(accentColor)
          .brighten(-10)
          .setAlpha(0.5)
          .toString();
        text = '#fff';
        break;
    }

    return {
      cssVariables: {
        [ColorSchemeCSSVariable.StandBackground]: standBackground,
        [ColorSchemeCSSVariable.DiskBackground]: diskBackground,
        [ColorSchemeCSSVariable.CapBackground]: capBackground,
        [ColorSchemeCSSVariable.EvenSectorBackground]: evenSectorBackground,
        [ColorSchemeCSSVariable.OddSectorBackground]: oddSectorBackground,
        [ColorSchemeCSSVariable.SectorBackgroundGradientStart]:
          sectorBackgroundGradientStart,
        [ColorSchemeCSSVariable.SectorBackgroundGradientEnd]:
          sectorBackgroundGradientEnd,
        [ColorSchemeCSSVariable.PointerTopLayerBackground]:
          pointerTopLayerBackground,
        [ColorSchemeCSSVariable.PointerTopLayerBorder]: pointerTopLayerBorder,
        [ColorSchemeCSSVariable.PointerBottomLayerBackground]:
          pointerBottomLayerBackground,
        [ColorSchemeCSSVariable.Text]: text
      }
    };
  }, [accentColor]);
}
